// The group cache is a simple browser-session cache of the group list and menu items we know about these groups.
// It is used to speed up loading and slug<->id lookups. When the app starts, the App component calls initialFetch().
// Then individual components can await waitForData() to ensure that the group cache is ready beforehand.
// If components need to be notified of changes, they can watch getCacheRef() and react to changes.

import { inject, ref } from 'vue';
import Page from '@/pages';

const GroupCache = {
	_cache: ref({}),		// Cache of group objects by id. This is a ref so it can be watched by other components.
	_promise: null,			// Promise that resolves when the group cache is ready.
	$http: null,			// HTTP client to use for fetching group list. Set the first time fetchGroupList is called - which must be in a setup().
	
	// Called initially by App component to fetch group from API backend, once or on manual refresh.
	fetchGroupList() {
		if (!this.$http)
			this.$http = inject('$http');

		this._promise = new Promise(resolve => {
			// TODO: Error handling for this call!
			this.$http.get('/group/menu').then(response => {
				// Load each group object into our cache, as well as processing the pages array to instantiate Page objects.
				// Note that the 'page objects' here in the menu not full page objects, just stubs with minimal data. We don't cache full page details ever.
				this._cache.value = {};
				for (const group of response.data) {
					this._cache.value[group.id] = group;
					group.pages = group.pages.map(page => new Page(page));
				}
				resolve();
			}).catch(error => {
				console.error('Error fetching group list:', error);
				resolve();
			});
		});
	},

	// Await'ed by other components to wait for cache ready. Note that this MUST be done in a setup or later
	// lifecycle hook so App base component has a chance to call fetchGroupList to setup the promise first!
	waitForData() {
		return this._promise;
	},

	// Get a group object by its ID - if it is intended to be changed, it MUST be cloned first!
	getById(id) {
		return this._cache.value[id] ?? null;
	},

	// Get a group object by its slug - if it is intended to be changed, it MUST be cloned first!
	getBySlug(slug) {
		return Object.values(this._cache.value).find(group => group.slug === slug) ?? null;
	},

	// Get the underlying cache ref for watching purposes only!
	getCacheRef() {
		return this._cache;
	},

	// Get all groups and pages as a tree structure suitable for rendering in a DxTreeView.
	// Each group must be tagged with the _menuTree property for replacement and context menu handling.
	// Note that because this is added directly, we MUST ensure our paths start with a slash!
	getMenuTree() {
		return Object.values(this._cache.value).map(group => {
			let pages = [];
			if (Array.isArray(group.pages) && group.pages.length > 0) {
				pages = group.pages.map(page => {
					let pageSettings = typeof page.settings == 'object' ? page.settings : JSON.parse(page.settings);
					return {
						text: pageSettings.menuLabel ? pageSettings.menuLabel : page.title,
						path: `/${group.slug}/${page.slug}`,
						icon: pageSettings.menuIcon ? pageSettings.menuIcon : 'blank',
						'_menuTree': [ group, page ],
						_menuWeight: pageSettings.menuWeight ?? 0
					};
				}).sort((a, b) => a._menuWeight - b._menuWeight);
			}
			pages.push({
				text: "(New Page)",
				path: `/${group.slug}/_new`,
				icon: "add"
			});
			return {
				text: group.title,
				path: `/${group.slug}/`,
				icon: "folder",
				items: pages,
				'_menuTree': [ group, null ],
				_menuWeight: group.settings?.menuWeight ?? 0
			};
		}).sort((a, b) => a._menuWeight - b._menuWeight);
	},
};

export default GroupCache;